import { useEffect, useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useOptionalTemaRoute } from "../../routes/TemaRouteContext";
import {
  TELEMETRY_CATEGORIES,
  TELEMETRY_EVENTS,
  initAppInsightsTransport,
  setTelemetryContextResolver,
  trackError,
  trackEvent,
} from "../../telemetry";

export function TelemetryProvider({ children }) {
  const { teacherId } = useAuth();
  const routeContext = useOptionalTemaRoute();
  const sessionStartedRef = useRef(false);

  const routeContextValue = useMemo(
    () => ({
      routeId: routeContext?.route?.id,
      toolId: routeContext?.isWorkspaceRoute ? routeContext?.route?.id : undefined,
      workspace: Boolean(routeContext?.isWorkspaceRoute),
      hash: routeContext?.hash,
    }),
    [
      routeContext?.hash,
      routeContext?.isWorkspaceRoute,
      routeContext?.route?.id,
    ],
  );

  useEffect(() => {
    void initAppInsightsTransport();
  }, []);

  useEffect(() => {
    setTelemetryContextResolver(() => ({
      teacherId,
      ...routeContextValue,
    }));

    return () => {
      setTelemetryContextResolver(() => ({}));
    };
  }, [routeContextValue, teacherId]);

  useEffect(() => {
    if (sessionStartedRef.current) {
      return undefined;
    }

    sessionStartedRef.current = true;
    trackEvent(
      TELEMETRY_EVENTS.SESSION_STARTED,
      {
        authenticated: Boolean(teacherId),
      },
      {
        category: TELEMETRY_CATEGORIES.WORKSPACE,
        dedupeKey: teacherId ?? "anonymous",
      },
    );

    function handleWindowError(event) {
      trackError(event.error ?? event.message, {
        source: "window.error",
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      });
    }

    function handleUnhandledRejection(event) {
      trackError(event.reason, {
        source: "window.unhandledrejection",
      });
    }

    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, [teacherId]);

  return children;
}
